/**
 * Nail reveal — a before/after comparison slider.
 *
 * The whole effect is one CSS custom property. `--nail-reveal-clip` drives the
 * clip-path on the top image, the seam and the knob, so a single write moves all
 * three in step.
 *
 * Two controls, one value. The frame handles the pointer — mouse, touch and pen
 * through the same path — and the <input type="range"> underneath it carries the
 * value, the keyboard bindings and the screen reader semantics.
 *
 * The range used to handle the pointer too, with a 2px transparent thumb so the
 * seam tracked the cursor exactly. That cannot work on iOS: WebKit only starts a
 * slider drag when the touch lands inside the thumb's box, so a finger never
 * found the 2px strip and dragging silently degraded to tap-to-jump. Owning the
 * gesture fixes that everywhere at once and keeps the seam exactly under the
 * finger at every position, including both ends.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// A touch drifts a pixel or two before it is a drag. Below this it is a tap.
const TAP_SLOP = 6;

class NailReveal extends HTMLElement {
  connectedCallback() {
    this.range = this.querySelector('[data-nail-reveal-range]');
    this.frame = this.querySelector('.custom-nail-reveal__frame');
    if (!this.range || !this.frame) return;

    this.afterLabel = this.dataset.afterLabel || 'applied';
    this.paintId = null;
    this.teaserId = null;
    this.observer = null;
    this.engaged = false;
    this.pointerId = null;
    this.dragging = false;
    this.startX = 0;

    this.onInput = this.onInput.bind(this);
    this.onEngage = this.onEngage.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);

    // Keyboard and assistive tech reach the value through the input.
    this.range.addEventListener('input', this.onInput);
    this.range.addEventListener('keydown', this.onEngage);

    this.frame.addEventListener('pointerdown', this.onPointerDown);
    this.frame.addEventListener('pointermove', this.onPointerMove);
    this.frame.addEventListener('pointerup', this.onPointerUp);
    this.frame.addEventListener('pointercancel', this.onPointerCancel);

    this.paint(this.range.valueAsNumber);
    this.watchForTeaser();
  }

  disconnectedCallback() {
    // Sections are torn down and re-rendered on every theme editor change.
    this.range?.removeEventListener('input', this.onInput);
    this.range?.removeEventListener('keydown', this.onEngage);
    this.frame?.removeEventListener('pointerdown', this.onPointerDown);
    this.frame?.removeEventListener('pointermove', this.onPointerMove);
    this.frame?.removeEventListener('pointerup', this.onPointerUp);
    this.frame?.removeEventListener('pointercancel', this.onPointerCancel);
    this.observer?.disconnect();
    if (this.paintId) cancelAnimationFrame(this.paintId);
    if (this.teaserId) cancelAnimationFrame(this.teaserId);
    this.observer = null;
    this.paintId = null;
    this.teaserId = null;
    this.pointerId = null;
    this.dragging = false;
  }

  onInput() {
    this.onEngage();
    this.paint(this.range.valueAsNumber);
  }

  onPointerDown(event) {
    if (event.button > 0) return;
    if (this.pointerId !== null) return; // one pointer drives the seam at a time

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    // A mouse or pen press is unambiguous, so it grabs the seam straight away.
    // A touch might still turn out to be a scroll, so it waits for the slop.
    this.dragging = event.pointerType !== 'touch';

    // Throws if the pointer is already gone; the drag still works without it.
    try {
      this.frame.setPointerCapture(event.pointerId);
    } catch {}
    if (event.cancelable) event.preventDefault();
    // preventDefault suppresses the focus that the press would have given, and
    // the knob's focus ring is drawn from the input's focus state.
    this.range.focus({ preventScroll: true });

    if (this.dragging) {
      this.frame.classList.add('is-dragging');
      this.onEngage();
      this.seek(event.clientX);
    }
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    if (!this.dragging) {
      if (Math.abs(event.clientX - this.startX) < TAP_SLOP) return;
      this.dragging = true;
      this.frame.classList.add('is-dragging');
      this.onEngage();
    }

    this.seek(event.clientX);
  }

  /** A press that never became a drag is a tap: jump the seam to it. */
  onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    if (!this.dragging) {
      this.onEngage();
      this.seek(event.clientX);
    }
    this.release();
  }

  /** The browser claimed the gesture — a vertical swipe scrolling the page. */
  onPointerCancel(event) {
    if (event.pointerId !== this.pointerId) return;
    this.release();
  }

  release() {
    this.pointerId = null;
    this.dragging = false;
    this.frame.classList.remove('is-dragging');
  }

  /**
   * Map a viewport x onto the frame's padding box — the box the images, the
   * seam and the knob are all positioned against — so the seam lands under the
   * pointer rather than near it.
   */
  seek(clientX) {
    const width = this.frame.clientWidth;
    if (!width) return;

    const left = this.frame.getBoundingClientRect().left + this.frame.clientLeft;
    const percentage = Math.min(100, Math.max(0, ((clientX - left) / width) * 100));

    // Let the input round to its own step, then paint what it actually holds.
    this.range.value = String(percentage);
    this.paint(this.range.valueAsNumber);
  }

  /** First real interaction stops the teaser and retires the hint. */
  onEngage() {
    if (this.engaged) return;
    this.engaged = true;
    if (this.teaserId) {
      cancelAnimationFrame(this.teaserId);
      this.teaserId = null;
    }
    this.observer?.disconnect();
    this.classList.add('is-engaged');
  }

  /** Coalesce writes so a fast drag paints once per frame, not once per event. */
  paint(percentage) {
    if (this.paintId) cancelAnimationFrame(this.paintId);
    this.paintId = requestAnimationFrame(() => {
      this.paintId = null;
      this.style.setProperty('--nail-reveal-clip', `${percentage}%`);
      this.range.setAttribute('aria-valuetext', `${Math.round(percentage)}% ${this.afterLabel}`);
    });
  }

  /**
   * Nothing about a still image says "drag me", so sweep the seam once when the
   * section first scrolls into view. Anyone who has asked for less motion, or
   * who reaches the widget and starts dragging first, never sees it.
   */
  watchForTeaser() {
    if (this.dataset.teaser !== 'true') return;
    if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.playTeaser();
      },
      { threshold: 0.55 }
    );
    this.observer.observe(this);
  }

  playTeaser() {
    const from = this.range.valueAsNumber;
    const distance = Number(this.dataset.teaserDistance) || 20;
    const duration = 1400;
    const startedAt = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      // A half sine carries the seam out and brings it back to where it started.
      const value = from + distance * Math.sin(progress * Math.PI);
      this.range.value = String(value);
      this.paint(value);

      if (progress < 1) {
        this.teaserId = requestAnimationFrame(step);
      } else {
        this.teaserId = null;
        this.range.value = String(from);
        this.paint(from);
      }
    };

    this.teaserId = requestAnimationFrame(step);
  }
}

if (!customElements.get('nail-reveal')) {
  customElements.define('nail-reveal', NailReveal);
}
